/**
 * Capability model.
 *
 * Roles are data, not code: the matrix below is only the *seed*. Admins edit the live
 * matrix in Firestore (`roles/{roleId}.capabilities`) from the Admin tab, and the
 * security rules read that same document. Changing a toggle in the UI changes what the
 * server enforces — there is no second copy to keep in sync.
 *
 * The one thing that is NOT data: anyone signing in from the Milestone domain is an
 * admin, hard-coded here and in firestore.rules. That is deliberate. If the roles
 * collection is ever emptied or mis-edited, Milestone can still get in and repair it.
 */

export const MILESTONE_DOMAIN = 'milestoneconsulting.tech'
export const CLIENT_DOMAIN = 'missionalmarketing.com'

export const CAPABILITIES = {
  'roadmap.view': 'See the roadmap, phases and gates',
  'activity.view': 'See the Activity History',
  'roadmap.edit': 'Change phase status, gate status and dates',
  'roadmap.complete': 'Mark a task the client owns as done',
  'hours.view': 'See hours logged and burn against the baseline',
  'contract.view': 'See the contract card and open the signed agreement',
  'decisions.view': 'See the decision register',
  'decisions.answer': 'Write a proposed answer on a decision',
  'decisions.decide': 'Mark a decision final (status → Decided)',
  'decisions.edit': 'Change a decision question, owner, priority or target date',
  'decisions.create': 'Add a new decision to the register',
  'decisions.comment': 'Comment on a decision',
  'comments.edit': 'Correct the wording of your own comment',
  'comments.delete': 'Delete your own comment',
  'comments.react': 'React to a comment with an emoji',
  'fields.view': 'See the field decisions guide',
  'fields.decide': 'Set migrate / do not migrate on a field',
  'fields.build': 'Record whether a migrating field is built, and where it lives in the new org',
  'products.view': 'See the product list',
  'products.edit': 'Answer the product list — keep, retire, pricing, bundles',
  'products.add': 'Add a product the Salesforce export missed',
  'products.delete': 'Remove a product that was added here',
  'owners.view': 'See the project owners list',
  'owners.edit': 'Name the person who owns a project role',
  'orgchart.view': 'See the org chart itself — its lanes, connectors and notes',
  'staff.view': 'See the staff roster, and the people drawn on the chart',
  'staff.edit': 'Add, edit and remove people on the staff roster',
  'orgchart.edit': 'Create containers, move people between them, and edit the chart notes',
  'sharingrule.view': 'See the sharing rules table',
  'sharingrule.edit': 'Add, edit and remove sharing rules',
  'content.edit': 'Correct what an item says — the question, the wording, the owner, a date',
  'links.add': 'Attach a link to an item',
  'links.edit': 'Correct what an attached link says or points at',
  'links.move': 'Change the order links attached to an item are read in',
  'links.remove': 'Remove an attached link',
  'users.manage': 'Invite people and assign roles',
  'permissions.manage': 'Edit the capability matrix itself',
} as const

export type Capability = keyof typeof CAPABILITIES
export type RoleId = 'admin' | 'clientLead' | 'clientContributor' | 'viewer'

export const ROLE_LABELS: Record<RoleId, string> = {
  admin: 'Administrator (Milestone)',
  clientLead: 'Client Lead',
  clientContributor: 'Client Contributor',
  viewer: 'Viewer',
}

const all = Object.keys(CAPABILITIES) as Capability[]
const grant = (...caps: Capability[]) =>
  Object.fromEntries(all.map((c) => [c, caps.includes(c)])) as Record<Capability, boolean>

/** Seed matrix. Editable in-app afterwards — these are starting points, not fixed policy. */
export const SEED_ROLE_CAPABILITIES: Record<RoleId, Record<Capability, boolean>> = {
  admin: grant(...all),
  // Kevin: can settle a decision and manage his own people.
  clientLead: grant(
    'roadmap.view', 'roadmap.complete', 'activity.view', 'hours.view', 'contract.view',
    'decisions.view', 'decisions.answer', 'decisions.decide',
    'decisions.create', 'decisions.comment',
    'fields.view', 'fields.decide',
    'products.view', 'products.edit',
    'owners.view', 'owners.edit',
    'links.add', 'users.manage',
  ),
  // Sarah: answers and attaches, but a final "Decided" is the Lead's call.
  clientContributor: grant(
    'roadmap.view', 'roadmap.complete', 'activity.view', 'hours.view', 'contract.view',
    'decisions.view', 'decisions.answer', 'decisions.comment',
    'fields.view', 'fields.decide',
    'products.view', 'products.edit',
    'owners.view',
    'links.add',
  ),
  // Reading what was said about items they can already open changes nothing, so a
  // read-only role gets the history the same way it gets the items.
  viewer: grant('roadmap.view', 'activity.view', 'contract.view', 'decisions.view',
                'fields.view', 'products.view', 'owners.view'),
}

/**
 * `products.add`, `products.delete`, `links.edit`, `links.move`, `comments.edit`,
 * `comments.delete`, `comments.react` and `content.edit` are in no client role above. All of
 * them are on purpose.
 *
 * A discussion is a record of what was said and when. Editing or deleting inside it changes
 * that record, so it is handed out rather than arriving with the ability to comment — and
 * even then only over your own words.
 *
 * Reacting alters nothing, but it is still its own grant rather than something bundled into
 * commenting. Every capability here is one act, so that turning one on never quietly turns
 * on another: somebody who should be able to say 👍 on a decision need not thereby be able
 * to write on it, and the reverse.
 *
 * Attaching a file is evidence arriving and is the client's to do. Changing what an
 * attachment says, where it points, or the order the set is read in are edits to a record
 * of what was attached — the same distinction as answering a question versus rewriting it.
 *
 * Answering the product list is what the client was invited here to do. Putting a new row
 * on it, and taking one off, are different acts: the list is the agreed scope of the
 * migration, and a row that appears or disappears without anyone deciding it should is not
 * an answer but a change to the question. Both are granted person by person in Admin →
 * Roles, so somebody holds them because it was decided rather than because a job title
 * carried them along.
 *
 * Milestone holds all three by virtue of the domain.
 */

/**
 * `fields.build` is in no client role above either. It records the build of a migrating
 * field — Milestone's work in the new org — rather than an answer about it, so the client
 * reads it without writing it unless somebody is granted it in Admin → Roles.
 */

/**
 * `content.edit` is in no client role above, on purpose.
 *
 * It is the one capability that can rewrite what a question asked rather than answer it,
 * so it is granted person by person in Admin → Roles rather than arriving with a job
 * title. Milestone holds it by virtue of the domain.
 */

/**
 * The six org chart capabilities are in no client role above, on purpose.
 *
 * The roster is the client's own staff list — 35 names, their emails, their phone numbers
 * and what each of them is allowed to do in Salesforce. Sign-in here is domain-restricted
 * rather than invitation-based, so anybody at Missional Marketing can sign in; a role
 * default would therefore hand write access to the staff list to the entire staff list.
 *
 * Six rather than three because the Org Chart tab is three different readerships wearing
 * one name. The diagram is a picture of how the company is arranged and is the least
 * sensitive thing on the tab; the roster is personal data about 35 named people; the
 * sharing rules are a statement of who may read what in the new org, which is a security
 * design a client may want reviewed by people who have no business reading phone numbers.
 * Bundling all three behind `orgchart.view` meant a grant of any one of them was a grant of
 * all three, so each read is now its own capability — `orgchart.view` for the diagram,
 * `staff.view` for `/staff`, `sharingrule.view` for `/orgSharingRules` — and each write
 * likewise. A viewer holding only `sharingrule.view` gets the tab, the Sharing Rules
 * sub-tab and nothing else; a viewer with `orgchart.view` but not `staff.view` gets the
 * lanes drawn empty, which is the honest rendering of "the roster was never read".
 *
 * They are granted person by person in Admin -> Roles, the same as `content.edit` and
 * `fields.build`, and for the same reason: somebody holds them because it was decided, not
 * because a job title carried them along. Milestone holds all six by virtue of the domain.
 */

/**
 * The role a first sign-in creates for itself.
 *
 * Everyone starts read-only. Signing in establishes identity, not authority — an admin
 * grants anything beyond viewing. This matters precisely because sign-in is
 * domain-restricted: anyone at Missional Marketing can sign in, so a contributor default
 * would silently hand write access to their entire staff list rather than to the two
 * people this was built for.
 *
 * Milestone is the deliberate exception. Somebody has to be able to administer the tool
 * on day one, and it is the lock-out escape hatch if the roles data is ever mis-edited.
 */
export const DEFAULT_ROLE_FOR_DOMAIN = (email: string): RoleId =>
  email.toLowerCase().split('@')[1] === MILESTONE_DOMAIN ? 'admin' : 'viewer'

export const isMilestoneEmail = (email: string | null | undefined) =>
  !!email && email.toLowerCase().endsWith(`@${MILESTONE_DOMAIN}`)
